import React from "react";
import { useParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";

const DOCS = {
  termeni: {
    ro: {
      title: "Termeni și Condiții",
      body: [
        ["1. Informații generale", "Acest site (LAINER) este operat de SC LAINER SRL, cu sediul în România (CUI/J: de completat). Prin utilizarea site-ului și plasarea unei comenzi, ești de acord cu prezentele condiții."],
        ["2. Produse și precomandă", "Produsele afișate sunt, în prezent, disponibile în regim de PRECOMANDĂ. Livrarea estimată este de 3–4 săptămâni de la confirmarea comenzii. Imaginile au caracter informativ; pot exista mici diferențe de culoare."],
        ["3. Prețuri și plată", "Prețurile sunt afișate în RON (lei) și includ TVA. În prezent plata se face la livrare (ramburs). Ne rezervăm dreptul de a modifica prețurile fără notificare prealabilă."],
        ["4. Comenzi", "O comandă este considerată fermă după completarea datelor de livrare și confirmarea acesteia. Vei primi un email de confirmare cu detaliile comenzii."],
        ["5. Proprietate intelectuală", "Toate materialele (logo, imagini, texte) aparțin LAINER și nu pot fi reproduse fără acord scris."],
        ["6. Contact", "Pentru orice întrebare ne poți scrie la reallainer@gmail.com."],
      ],
    },
    en: {
      title: "Terms & Conditions",
      body: [
        ["1. General information", "This site (LAINER) is operated by SC LAINER SRL, based in Romania. By using the site and placing an order you agree to these terms."],
        ["2. Products & pre-order", "Products are currently offered on a PRE-ORDER basis. Estimated delivery is 3–4 weeks from order confirmation. Images are illustrative; slight colour differences may occur."],
        ["3. Prices & payment", "Prices are shown in RON and include VAT. Payment is currently on delivery (cash on delivery). We reserve the right to change prices without prior notice."],
        ["4. Orders", "An order is firm once shipping details are provided and confirmed. You will receive a confirmation email with the order details."],
        ["5. Intellectual property", "All materials (logo, images, text) belong to LAINER and may not be reproduced without written consent."],
        ["6. Contact", "For any questions, email us at reallainer@gmail.com."],
      ],
    },
  },
  retur: {
    ro: {
      title: "Politica de Retur",
      body: [
        ["Drept de retragere", "Conform OUG nr. 34/2014, ai dreptul de a te retrage din contract în termen de 14 zile calendaristice de la primirea produsului, fără a fi nevoie să justifici decizia."],
        ["Retur gratuit 30 de zile", "La LAINER oferim retur GRATUIT în 30 de zile de la primire, dacă produsul este nepurtat, cu etichetele intacte și în ambalajul original."],
        ["Cum returnezi", "Ne scrii la reallainer@gmail.com cu numărul comenzii (LN-…). Îți trimitem instrucțiunile de retur. Rambursarea se face în maximum 14 zile de la primirea produsului returnat."],
        ["Excepții", "Produsele personalizate sau igienizabile pot fi exceptate de la retur, conform legii."],
      ],
    },
    en: {
      title: "Return Policy",
      body: [
        ["Right of withdrawal", "Under Romanian law (OUG 34/2014), you may withdraw from the contract within 14 calendar days of receiving the product, without giving a reason."],
        ["Free 30-day returns", "LAINER offers FREE returns within 30 days of receipt, provided the item is unworn, with tags intact and in the original packaging."],
        ["How to return", "Email us at reallainer@gmail.com with your order number (LN-…). We'll send return instructions. Refunds are issued within 14 days of receiving the returned item."],
        ["Exceptions", "Personalised or hygiene items may be excluded from returns, as permitted by law."],
      ],
    },
  },
  confidentialitate: {
    ro: {
      title: "Politica de Confidențialitate (GDPR)",
      body: [
        ["Operator de date", "SC LAINER SRL prelucrează datele tale personale conform Regulamentului (UE) 2016/679 (GDPR)."],
        ["Ce date colectăm", "Nume, email, telefon, adresă de livrare — necesare pentru procesarea și livrarea comenzilor, precum și pentru comunicări legate de comandă."],
        ["Scopul prelucrării", "Procesarea comenzilor, livrare, suport clienți și, cu acordul tău, newsletter."],
        ["Stocare și securitate", "Datele sunt stocate securizat și nu sunt vândute terților. Le partajăm doar cu firma de curierat pentru livrare."],
        ["Drepturile tale", "Ai dreptul de acces, rectificare, ștergere (dreptul de a fi uitat), restricționare și portabilitate. Ne poți scrie la reallainer@gmail.com."],
        ["Cookies", "Folosim cookies esențiale pentru funcționarea coșului și a sesiunii. Nu folosim tracking în scop publicitar fără acord."],
      ],
    },
    en: {
      title: "Privacy Policy (GDPR)",
      body: [
        ["Data controller", "SC LAINER SRL processes your personal data under Regulation (EU) 2016/679 (GDPR)."],
        ["What we collect", "Name, email, phone, shipping address — needed to process and deliver orders and for order-related communication."],
        ["Purpose", "Order processing, delivery, customer support and, with your consent, newsletter."],
        ["Storage & security", "Data is stored securely and never sold to third parties. We only share it with the courier for delivery."],
        ["Your rights", "You have the right to access, rectify, erase (right to be forgotten), restrict and port your data. Email us at reallainer@gmail.com."],
        ["Cookies", "We use essential cookies for cart and session. We do not use advertising tracking without consent."],
      ],
    },
  },
};

const Legal = () => {
  const { doc } = useParams();
  const { lang } = useLang();
  const content = useContent();
  const key = doc === "confidentialitate" ? "gdpr" : doc; // legal keys: termeni, retur, gdpr
  const custom = content?.legal?.[`${key}_${lang}`];
  const d = (DOCS[doc] || DOCS.termeni)[lang] || (DOCS[doc] || DOCS.termeni).ro;
  return (
    <main className="mx-auto max-w-2xl px-4 py-14 sm:px-6" data-testid={`legal-${doc}`}>
      <h1 className="text-2xl uc font-semibold sm:text-3xl">{d.title}</h1>
      {custom && custom.trim() ? (
        <div className="mt-8 whitespace-pre-line text-sm leading-relaxed text-muted-foreground" data-testid="legal-custom">{custom}</div>
      ) : (
        <div className="mt-8 space-y-6">
          {d.body.map(([h, p], i) => (
            <div key={i}>
              <h2 className="font-mono text-sm uppercase tracking-widest">{h}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p}</p>
            </div>
          ))}
        </div>
      )}
      <p className="mt-10 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        Ultima actualizare / Last updated: {new Date().getFullYear()}
      </p>
    </main>
  );
};

export default Legal;
